import { DownloadOutlined, FileAddOutlined } from "@ant-design/icons";
import { RouteComponentProps, useParams } from "@reach/router";
import {
  Typography,
  Table,
  Col,
  Row,
  Button,
  Space,
  DatePicker,
  Modal,
  notification,
  Tag,
  Tooltip,
} from "antd";
import FileSaver from "file-saver";
import { when } from "mobx";
import { observer } from "mobx-react-lite";
import moment from "moment";
import React, { useState, Key, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import XLSX from "xlsx";

import User from "../component/invitation/user";
import Cell from "../component/order/cell";
import OrderDetail from "../component/order/detail";
import { makeId } from "../helper/tools";
import {
  FilterData,
  OrderDirection,
  OrderField,
  Query,
  useStore,
  WorkOrder,
  PAGE_SIZE,
} from "../store";

const styles = {
  gutter: {
    padding: 8,
  },
  footer: {
    textAlign: "center" as const,
  },
};

interface Props extends RouteComponentProps {}
const OrderList = (props: Props) => {
  const [selected, setSelected] = useState<Key[]>([]);
  const [shownDetails, setShownDetails] = useState<WorkOrder>();
  const [query, setQuery] = useState<Query>({
    order: {
      field: "startAt",
      direction: "desc",
    },
    page: 1,
  });
  const store = useStore();
  const { multivers } = store;
  const { administrationId } = useParams();
  const administration = useMemo(
    () => multivers.administration(administrationId),
    [administrationId]
  );

  const { orders, products, customers, employees, locations } = administration;
  const { t } = useTranslation();

  useEffect(() => {
    administration.updateQuery(query);
  }, [query]);

  const newOrder = async () => {
    const orderId = makeId();
    const newOrder = new WorkOrder(
      `multivers/${administrationId}/workorder/${orderId}`,
      {},
      store
    );
    setShownDetails(newOrder);
  };

  const showDetails = (order: WorkOrder) => () => {
    setShownDetails(order);
  };
  const hideDetails = () => {
    setShownDetails(undefined);
  };

  const download = async () => {
    try {
      const fileName = `Hack Harvest - ${moment().format(
        "DD-MM-YYYY HH:mm"
      )}.xlsx`;
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const data = await downloadData();
      const ws = XLSX.utils.json_to_sheet(data, {
        header: downloadColumns,
        skipHeader: true,
      });
      const wb = { Sheets: { Sheet: ws }, SheetNames: ["Sheet"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const saveData = new Blob([excelBuffer], { type: fileType });
      await administration.downloaded(selected as string[]);
      FileSaver.saveAs(saveData, fileName);
    } catch (error) {
      const message = (error as Error)?.message;
      notification.error({
        message: t("Download failed"),
        description: message,
      });
    }
  };

  const data = orders.docs.slice();

  const columns = [
    {
      title: t("Start date"),
      dataIndex: "startAt",
      key: "startAt",
      render: (date: Date) => (
        <Cell
          data={{
            title: moment(date).format("LL"),
          }}
        />
      ),
      sorter: true,
      defaultSortOrder: "descend" as const,
      filteredValue: query?.filter?.["startAt"]
        ? [query.filter["startAt"]]
        : undefined,
      filterDropdown: (config: any) => (
        <>
          <Row>
            <Col style={styles.gutter}>
              <DatePicker.RangePicker
                value={config.selectedKeys[0]}
                disabledDate={(date) => !!date?.isAfter()}
                onCalendarChange={(dates) => config.setSelectedKeys([dates])}
              />
            </Col>
          </Row>
          <Row>
            <Col style={styles.gutter}>
              <Space>
                <Button
                  type="link"
                  onClick={() => {
                    config.setSelectedKeys([]);
                  }}
                  size="small"
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  onClick={() => config.confirm()}
                  size="small"
                >
                  Ok
                </Button>
              </Space>
            </Col>
          </Row>
        </>
      ),
    },
    {
      title: t("User"),
      dataIndex: "employee",
      key: "employee",
      render: (item: any) => <User data={item} />,
      sorter: true,
      filters: employees.docs.map((employee) => ({
        text: employee.name,
        value: employee.id || "",
      })),
      filterMultiple: false,
      filteredValue: query?.filter?.["employee"]
        ? [query.filter["employee"]]
        : undefined,
    },
    // {
    //   title: t("Start"),
    //   dataIndex: "startTime",
    //   key: "start",
    //   sorter: (a: WorkOrder, b: WorkOrder) =>
    //     a.startTime.localeCompare(b.startTime),
    // },
    // {
    //   title: t("End"),
    //   dataIndex: "endTime",
    //   key: "end",
    //   sorter: (a: WorkOrder, b: WorkOrder) =>
    //     a.endTime.localeCompare(b.endTime),
    // },
    {
      title: t("Hours"),
      dataIndex: "hours",
      key: "hours",
    },
    {
      title: t("Product"),
      dataIndex: "product",
      key: "product",
      render: (item: any) => <Cell data={item} />,
      sorter: true,
      filters: products.docs.map((product) => ({
        text: product.title,
        value: product.id || "",
      })),
      filterMultiple: false,
      filteredValue: query?.filter?.["product"]
        ? [query.filter["product"]]
        : undefined,
    },
    {
      title: t("Customer"),
      dataIndex: "customer",
      key: "customer",
      render: (item: any) => <Cell data={item} />,
      sorter: true,
      filters: customers.docs.map((customer) => ({
        text: customer.title,
        value: customer.id || "",
      })),
      filterMultiple: false,
      filteredValue: query?.filter?.["customer"]
        ? [query.filter["customer"]]
        : undefined,
    },
    {
      title: t("Location"),
      dataIndex: "location",
      key: "location",
      render: (item: any) => <Cell data={item} />,
      sorter: true,
      filters: locations.docs.map((location) => ({
        text: location.title,
        value: location.id || "",
      })),
      filterMultiple: false,
      filteredValue: query?.filter?.["location"]
        ? [query.filter["location"]]
        : undefined,
    },
    {
      title: t("Synced"),
      dataIndex: "syncedAt",
      key: "synced",
      render: (date: Date, order: WorkOrder) =>
        order.syncError ? (
          <Tooltip placement="top" title={order.syncError}>
            <Tag color="error">{t("Failed")}</Tag>
          </Tooltip>
        ) : (
          <Cell data={{ title: date ? moment(date).format("LLL") : "" }} />
        ),
    },
    {
      title: t("Downloaded"),
      dataIndex: "downloadedAt",
      key: "download",
      render: (date: Date) => (
        <Cell data={{ title: date ? moment(date).format("LLL") : "" }} />
      ),
      sorter: true,
      // filters: [
      //   {
      //     text: t("Not downloaded"),
      //     value: "notdownloaded",
      //   },
      // ],
      // filterMultiple: false,
    },
  ];

  const downloadData = async () =>
    new Promise<object[]>((resolve) => {
      const orders = data.filter(
        (order) => selected.indexOf(order.id || "") >= 0
      );

      when(
        () =>
          orders.reduce<boolean>(
            (acc, order) =>
              acc &&
              order.isLoaded &&
              (!order.employee || order.employee.isLoaded) &&
              (!order.customer || order.customer.isLoaded) &&
              (!order.vehicle || order.vehicle.isLoaded) &&
              (!order.location || order.location.isLoaded) &&
              (!order.product || order.product.isLoaded) &&
              (!order.location || order.location.isLoaded) &&
              (!order.hourType || order.hourType.isLoaded),
            true
          ),
        () => {
          resolve([
            {
              date: t("Date"),
              user: t("User"),
              start: t("Start"),
              end: t("End"),
              hours: t("Hours"),
              daynight: t("Day/night"),
              customer: t("Customer"),
              vehicle: t("Vehicle"),
              location: t("Location"),
              product: t("Product"),
              activity: t("Activity"),
              remarks: t("Remarks"),
            },
            ...orders.map((order) => ({
              date: moment(order.startAt).format("D-M-YYYY"),
              user: order.employee?.name,
              start: order.startTime,
              end: order.endTime,
              hours: order.duration.hours() + order.duration.minutes() / 60,
              daynight: order.dayNight === "day" ? t("Day") : t("Night"),
              customer: order.customer?.id,
              vehicle: order.vehicle?.id,
              location: order.location?.title,
              product: order.product?.id,
              activity: order.hourType?.title,
              remarks: order.remarks || "",
            })),
          ]);
        }
      );
    });

  const downloadColumns = [
    "date",
    "user",
    "start",
    "end",
    "hours",
    "daynight",
    "customer",
    "vehicle",
    "location",
    "product",
    "activity",
    "remarks",
  ];

  const rowHandler = (order: WorkOrder) => ({
    onClick: showDetails(order),
  });

  const loadMore = () => {
    setQuery({ ...query, page: (query?.page || 1) + 1 });
  };

  const renderMore = () =>
    data.length === (query?.page || 1) * PAGE_SIZE && (
      <div style={styles.footer}>
        <Button type="primary" onClick={loadMore} loading={orders.isLoading}>
          {t("Load more")}
        </Button>
      </div>
    );

  return (
    <>
      <Row>
        <Col flex="auto">
          <Typography.Title>{t("Workorders")}</Typography.Title>
        </Col>
        <Col flex="none">
          <Space>
            <Button
              icon={<DownloadOutlined />}
              disabled={selected.length <= 0}
              onClick={download}
            >
              {t("Export")}
            </Button>
            <Button
              type="primary"
              icon={<FileAddOutlined />}
              onClick={newOrder}
            >
              {t("Add work order")}
            </Button>
          </Space>
        </Col>
      </Row>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        rowSelection={{
          type: "checkbox",
          onChange: setSelected,
        }}
        loading={orders.isLoading}
        pagination={false}
        onRow={rowHandler}
        footer={renderMore}
        onChange={(pagination, filters, sorter) => {
          const filterFields = Object.keys(filters).filter(
            (field) => !!filters[field]
          );
          const shouldFilter = (field: string) => {
            const index = filterFields.indexOf(field);
            return index >= 0;
          };
          const { startAt, employee, product, customer, location } = filters;
          let newFilter: FilterData | undefined = {};
          if (shouldFilter("startAt") && startAt) {
            const dates = startAt[0] as any;
            if (Array.isArray(dates)) {
              const start = dates[0].startOf("day").toDate();
              const end = dates[1].endOf("day").toDate();
              newFilter["startAt"] = { start, end };
            } else {
              newFilter["startAt"] = dates;
            }
          }
          if (shouldFilter("employee") && employee) {
            newFilter["employee"] = employee[0] as string;
          }
          if (shouldFilter("product") && product) {
            newFilter["product"] = product[0] as string;
          }
          if (shouldFilter("customer") && customer) {
            newFilter["customer"] = customer[0] as string;
          }
          if (shouldFilter("location") && location) {
            newFilter["location"] = location[0] as string;
          }
          if (Object.keys(newFilter).length === 0) {
            newFilter = undefined;
          }
          let sort;
          if (sorter && !Array.isArray(sorter)) {
            const field = sorter.field as OrderField;
            const direction: OrderDirection =
              sorter.order === "descend" ? "desc" : "asc";
            if (field && direction) {
              sort = {
                field,
                direction,
              };
            }
          }
          setQuery({ filter: newFilter, order: sort, page: query?.page });
        }}
      />
      <Modal
        title={t("Workorder")}
        visible={!!shownDetails}
        footer={false}
        closable={false}
        width="80%"
      >
        {shownDetails && (
          <OrderDetail
            administration={administration}
            order={shownDetails}
            onCancel={hideDetails}
          />
        )}
      </Modal>
    </>
  );
};

export default observer(OrderList);
